import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Settings, History, Star, MapPin, Phone, Mail, Edit, Eye, X, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CustomerAuthModal from '../components/CustomerAuthModal';
import { getBackendURL } from '../utils/config';

const CustomerPortal = () => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [modifyForm, setModifyForm] = useState({
    start_time: '',
    end_time: '',
    location: '',
    special_requests: ''
  });
  const [loyaltyData, setLoyaltyData] = useState({
    current_points: 0,
    tier: 'Silver',
    points_to_next_tier: 5000,
    recent_transactions: [],
    available_redemptions: []
  });
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    preferences: {
      dietary_restrictions: '',
      favorite_cuisines: [],
      preferred_times: [],
      special_occasions: '',
      communication_preference: 'email'
    }
  });
  
  const { isCustomerAuthenticated, customer, getCustomerAuthHeaders, customerLogout, loading: authLoading } = useAuth();

  // Get backend URL safely
  const backendUrl = getBackendURL();

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (authLoading) {
        console.log('Auth loading timeout, showing auth modal');
        setShowAuthModal(true);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [authLoading]);

  useEffect(() => {
    // Check if user is authenticated
    console.log('CustomerPortal: useEffect triggered');
    console.log('Customer authenticated:', isCustomerAuthenticated());
    console.log('Loading:', loading);
    
    if (isCustomerAuthenticated()) {
      console.log('Customer is authenticated, loading data');
      loadUserBookings();
      loadUserPreferences();
      loadLoyaltyData();
    } else {
      console.log('Customer not authenticated, showing auth modal');
      setShowAuthModal(true);
    }
  }, [isCustomerAuthenticated]);

  const loadUserBookings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/customer/bookings`, {
        headers: getCustomerAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      } else {
        console.error('Failed to load bookings');
        // Fallback to empty array
        setBookings([]);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPreferences = async () => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/customer/profile`, {
        headers: getCustomerAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      } else {
        console.error('Failed to load user profile');
        // Fallback to customer data from auth context
        if (customer) {
          setUserInfo({
            name: customer.name,
            email: customer.email,
            phone: customer.phone || '',
            city: customer.city || '',
            state: customer.state || '',
            preferences: {
              dietary_restrictions: '',
              favorite_cuisines: [],
              preferred_times: [],
              special_occasions: '',
              communication_preference: 'email'
            },
            loyalty_points: customer.loyalty_points || 0,
            tier: customer.tier || 'Silver',
            member_since: customer.member_since || new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#22c55e';
      case 'pending': return '#f59e0b';
      case 'completed': return '#3b82f6';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'private-dining': return '🍽️';
      case 'travel-culinary': return '✈️';
      case 'wellness-retreat': return '🧘';
      case 'corporate-event': return '🏢';
      default: return '🌟';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePreferenceUpdate = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [field]: value
      }
    }));
  };

  const savePreferences = async () => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/customer/profile`, {
        method: 'PUT',
        headers: getCustomerAuthHeaders(),
        body: JSON.stringify({
          name: userInfo.name,
          phone: userInfo.phone,
          city: userInfo.city,
          state: userInfo.state,
          preferences: userInfo.preferences
        })
      });

      if (response.ok) {
        alert('Preferences saved successfully!');
      } else {
        alert('Failed to save preferences. Please try again.');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences. Please try again.');
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // Reload data after successful authentication
    loadUserBookings();
    loadUserPreferences();
    loadLoyaltyData();
  };

  const handleLogout = async () => {
    await customerLogout();
    setShowAuthModal(true);
    setBookings([]);
    setUserInfo({
      name: '',
      email: '',
      phone: '',
      city: '',
      state: '',
      preferences: {
        dietary_restrictions: '',
        favorite_cuisines: [],
        preferred_times: [],
        special_occasions: '',
        communication_preference: 'email'
      }
    });
  };

  const loadLoyaltyData = async () => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/customer/loyalty`, {
        headers: getCustomerAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setLoyaltyData(data);
      } else {
        console.error('Failed to load loyalty data');
      }
    } catch (error) {
      console.error('Error loading loyalty data:', error);
    }
  };

  const handleBookingAction = (booking, action) => {
    setSelectedBooking(booking);
    if (action === 'view') {
      setShowBookingDetails(true);
    } else if (action === 'cancel') {
      setShowCancelModal(true);
    } else if (action === 'modify') {
      // Pre-populate form with existing booking data
      setModifyForm({
        start_time: booking.booking_date ? new Date(booking.booking_date).toISOString().slice(0, 16) : '',
        end_time: '',
        location: booking.location || '',
        special_requests: booking.special_requests || booking.dietary_restrictions || ''
      });
      setShowModifyModal(true);
    } else if (action === 'rate') {
      setShowRatingModal(true);
    }
  };

  const cancelBooking = async () => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(
        `${backendUrl}/api/customer/booking/${selectedBooking.service_type}/${selectedBooking.id}/cancel`,
        {
          method: 'POST',
          headers: getCustomerAuthHeaders()
        }
      );

      if (response.ok) {
        alert('Booking cancelled successfully');
        setShowCancelModal(false);
        setSelectedBooking(null);
        loadUserBookings(); // Reload bookings
      } else {
        alert('Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Error cancelling booking');
    }
  };

  const modifyBooking = async () => {
    try {
      const backendUrl = getBackendURL();
      
      // Prepare update data (only send non-empty fields)
      const updateData = {};
      if (modifyForm.start_time) {
        updateData.booking_date = new Date(modifyForm.start_time).toISOString();
        updateData.start_time = new Date(modifyForm.start_time).toISOString();
      }
      if (modifyForm.end_time) {
        updateData.end_time = new Date(modifyForm.end_time).toISOString();
      }
      if (modifyForm.location) {
        updateData.location = modifyForm.location;
      }
      if (modifyForm.special_requests) {
        updateData.special_requests = modifyForm.special_requests;
        updateData.dietary_restrictions = modifyForm.special_requests; // Some services use this field
      }

      // Try customer-specific booking update endpoint first
      let response = await fetch(
        `${backendUrl}/api/customer/booking/${selectedBooking.service_type}/${selectedBooking.id}/modify`,
        {
          method: 'PUT',
          headers: {
            ...getCustomerAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        }
      );

      // If that doesn't work, fall back to calendar endpoint for calendar bookings
      if (!response.ok && selectedBooking.service_type === 'calendar') {
        response = await fetch(
          `${backendUrl}/api/calendar/bookings/${selectedBooking.id}`,
          {
            method: 'PUT',
            headers: {
              ...getCustomerAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          }
        );
      }

      if (response.ok) {
        alert('Booking modified successfully! You will receive a confirmation email shortly.');
        setShowModifyModal(false);
        setSelectedBooking(null);
        setModifyForm({
          start_time: '',
          end_time: '',
          location: '',
          special_requests: ''
        });
        loadUserBookings(); // Reload bookings
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to modify booking: ${errorData.detail || 'This booking type may not support modifications. Please contact us directly.'}`);
      }
    } catch (error) {
      console.error('Error modifying booking:', error);
      alert('Error modifying booking. Please contact us directly to make changes.');
    }
  };

  const submitRating = async () => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(
        `${backendUrl}/api/customer/booking/${selectedBooking.service_type}/${selectedBooking.id}/rate`,
        {
          method: 'POST',
          headers: getCustomerAuthHeaders(),
          body: JSON.stringify({
            rating: rating,
            comment: reviewComment
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`Rating submitted successfully! ${result.loyalty_points_earned} loyalty points earned.`);
        setShowRatingModal(false);
        setSelectedBooking(null);
        setRating(0);
        setReviewComment('');
        loadUserBookings(); // Reload bookings
        loadLoyaltyData(); // Reload loyalty data
      } else {
        alert('Failed to submit rating');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      alert('Error submitting rating');
    }
  };

  const renderBookingHistory = () => (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <h2 style={{
          color: '#ffffff',
          fontSize: '24px',
          fontWeight: '600',
          margin: '0',
          fontFamily: "'Playfair Display', serif"
        }}>
          My Bookings
        </h2>
        <button
          onClick={() => window.location.href = '/booking'}
          style={{
            background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
            color: '#000000',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={16} />
          New Booking
        </button>
      </div>

      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          Loading your bookings...
        </div>
      ) : bookings.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(212, 175, 55, 0.2)'
        }}>
          <Calendar size={48} style={{ color: '#d4af37', marginBottom: '20px' }} />
          <h3 style={{
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            No Bookings Yet
          </h3>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '30px'
          }}>
            Start your luxury culinary journey by making your first booking
          </p>
          <button
            onClick={() => window.location.href = '/booking'}
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
              color: '#000000',
              border: 'none',
              padding: '14px 28px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Book Your First Experience
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {bookings.map((booking) => (
            <div
              key={booking.id}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '24px',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>
                    {getServiceIcon(booking.service_type)}
                  </span>
                  <div>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: '18px',
                      fontWeight: '600',
                      margin: '0 0 4px 0'
                    }}>
                      {booking.service_name}
                    </h3>
                    <span
                      style={{
                        color: getStatusColor(booking.status),
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        backgroundColor: `${getStatusColor(booking.status)}20`,
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}
                    >
                      {booking.status}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      padding: '8px',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <Eye size={16} />
                  </button>
                  {booking.status === 'pending' && (
                    <button
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        color: '#ffffff',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Edit size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div>
                  <span style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '500'
                  }}>
                    Date
                  </span>
                  <p style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    margin: '4px 0 0 0'
                  }}>
                    {booking.event_date && formatDate(booking.event_date)}
                    {booking.travel_dates && formatDate(booking.travel_dates)}
                    {booking.preferred_dates && formatDate(booking.preferred_dates)}
                  </p>
                </div>

                {booking.preferred_time && (
                  <div>
                    <span style={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '500'
                    }}>
                      Time
                    </span>
                    <p style={{
                      color: '#ffffff',
                      fontSize: '14px',
                      margin: '4px 0 0 0'
                    }}>
                      {booking.preferred_time}
                    </p>
                  </div>
                )}

                <div>
                  <span style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '500'
                  }}>
                    Guests
                  </span>
                  <p style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    margin: '4px 0 0 0'
                  }}>
                    {booking.guest_count || booking.group_size || booking.participants}
                  </p>
                </div>

                {(booking.total_cost || booking.estimated_cost) && (
                  <div>
                    <span style={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '500'
                    }}>
                      {booking.total_cost ? 'Total Cost' : 'Estimated Cost'}
                    </span>
                    <p style={{
                      color: '#d4af37',
                      fontSize: '16px',
                      fontWeight: '600',
                      margin: '4px 0 0 0'
                    }}>
                      ${(booking.total_cost || booking.estimated_cost).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {booking.advanced_features && booking.advanced_features.length > 0 && (
                <div style={{
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  paddingTop: '16px',
                  marginTop: '16px'
                }}>
                  <span style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Advanced Features
                  </span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {booking.advanced_features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: 'rgba(212, 175, 55, 0.2)',
                          color: '#d4af37',
                          fontSize: '11px',
                          fontWeight: '500',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          textTransform: 'capitalize'
                        }}
                      >
                        {feature.replace('-', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {booking.status === 'completed' && booking.rating_given && (
                <div style={{
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  paddingTop: '16px',
                  marginTop: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Star size={16} style={{ color: '#d4af37', fill: '#d4af37' }} />
                  <span style={{ color: '#d4af37', fontSize: '14px', fontWeight: '500' }}>
                    Rated {booking.rating_given}/5 stars
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '16px',
                marginTop: '16px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => handleBookingAction(booking, 'view')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'rgba(255, 255, 255, 0.8)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Eye size={14} />
                  View Details
                </button>

                {(booking.status === 'pending' || booking.status === 'confirmed') && (
                  <>
                    <button
                      onClick={() => handleBookingAction(booking, 'modify')}
                      style={{
                        background: 'rgba(212, 175, 55, 0.1)',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        color: '#d4af37',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Edit size={14} />
                      Modify
                    </button>
                    <button
                      onClick={() => handleBookingAction(booking, 'cancel')}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#ef4444',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <X size={14} />
                      Cancel
                    </button>
                  </>
                )}

                {booking.status === 'completed' && !booking.rating_given && (
                  <button
                    onClick={() => handleBookingAction(booking, 'rate')}
                    style={{
                      background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                      border: 'none',
                      color: '#000000',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Star size={14} />
                    Rate Experience
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderLoyaltySection = () => (
    <div>
      <h2 style={{
        color: '#ffffff',
        fontSize: '24px',
        fontWeight: '600',
        marginBottom: '30px',
        fontFamily: "'Playfair Display', serif"
      }}>
        Loyalty Rewards
      </h2>

      {/* Loyalty Overview */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div style={{
          backgroundColor: 'rgba(212, 175, 55, 0.1)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <Star size={32} style={{ color: '#d4af37', fill: '#d4af37', marginBottom: '12px' }} />
          <h3 style={{ color: '#d4af37', fontSize: '18px', marginBottom: '8px' }}>Current Points</h3>
          <p style={{ color: '#ffffff', fontSize: '28px', fontWeight: '600', margin: 0 }}>{loyaltyData.current_points}</p>
        </div>

        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <User size={32} style={{ color: '#d4af37', marginBottom: '12px' }} />
          <h3 style={{ color: '#d4af37', fontSize: '18px', marginBottom: '8px' }}>Current Tier</h3>
          <p style={{ color: '#ffffff', fontSize: '20px', fontWeight: '600', margin: 0 }}>{loyaltyData.tier}</p>
          {loyaltyData.points_to_next_tier > 0 && (
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginTop: '4px' }}>
              {loyaltyData.points_to_next_tier} points to next tier
            </p>
          )}
        </div>
      </div>

      {/* Available Redemptions */}
      {loyaltyData.available_redemptions && loyaltyData.available_redemptions.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '20px'
          }}>
            Available Redemptions
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {loyaltyData.available_redemptions.map((redemption, index) => (
              <div key={index} style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h4 style={{ color: '#ffffff', fontSize: '16px', margin: '0 0 4px 0' }}>{redemption.name}</h4>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', margin: '0 0 4px 0' }}>
                    {redemption.description}
                  </p>
                  <p style={{ color: '#d4af37', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                    {redemption.points} points
                  </p>
                </div>
                <button style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                  border: 'none',
                  color: '#000000',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}>
                  Redeem
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {loyaltyData.recent_transactions && loyaltyData.recent_transactions.length > 0 && (
        <div>
          <h3 style={{
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '20px'
          }}>
            Recent Activity
          </h3>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {loyaltyData.recent_transactions.map((transaction, index) => (
              <div key={index} style={{
                padding: '16px',
                borderBottom: index < loyaltyData.recent_transactions.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>{transaction.description}</p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', margin: 0 }}>
                    {new Date(transaction.date).toLocaleDateString()}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{
                    color: transaction.type === 'earn' ? '#10b981' : '#ef4444',
                    fontSize: '14px',
                    fontWeight: '600',
                    margin: '0 0 4px 0'
                  }}>
                    {transaction.type === 'earn' ? '+' : '-'}{transaction.points} pts
                  </p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', margin: 0, textTransform: 'capitalize' }}>
                    {transaction.type}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!loyaltyData.recent_transactions || loyaltyData.recent_transactions.length === 0) && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          <Star size={48} style={{ color: 'rgba(212, 175, 55, 0.3)', marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>Start earning loyalty points!</p>
          <p style={{ fontSize: '14px' }}>Make bookings and leave reviews to earn points and unlock exclusive rewards.</p>
        </div>
      )}
    </div>
  );

  const renderPreferences = () => (
    <div>
      <h2 style={{
        color: '#ffffff',
        fontSize: '24px',
        fontWeight: '600',
        marginBottom: '30px',
        fontFamily: "'Playfair Display', serif"
      }}>
        My Preferences
      </h2>

      <div style={{ display: 'grid', gap: '24px' }}>
        {/* Personal Information */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <User size={20} />
            Personal Information
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            <div>
              <label style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                display: 'block'
              }}>
                Full Name
              </label>
              <input
                type="text"
                value={userInfo.name}
                onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                display: 'block'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={userInfo.email}
                onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                display: 'block'
              }}>
                Phone Number
              </label>
              <input
                type="tel"
                value={userInfo.phone}
                onChange={(e) => setUserInfo(prev => ({ ...prev, phone: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                display: 'block'
              }}>
                Location
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="City"
                  value={userInfo.city}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, city: e.target.value }))}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  placeholder="State"
                  value={userInfo.state}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, state: e.target.value }))}
                  style={{
                    width: '80px',
                    padding: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Culinary Preferences */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Settings size={20} />
            Culinary Preferences
          </h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                display: 'block'
              }}>
                Dietary Restrictions
              </label>
              <textarea
                value={userInfo.preferences.dietary_restrictions}
                onChange={(e) => handlePreferenceUpdate('dietary_restrictions', e.target.value)}
                placeholder="e.g., Vegetarian, No shellfish, Gluten-free"
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
              <label style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                display: 'block'
              }}>
                Favorite Cuisines
              </label>
              <input
                type="text"
                value={userInfo.preferences.favorite_cuisines.join(', ')}
                onChange={(e) => handlePreferenceUpdate('favorite_cuisines', e.target.value.split(', ').filter(c => c.trim()))}
                placeholder="e.g., Italian, Mediterranean, Indian Fusion"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                  display: 'block'
                }}>
                  Preferred Dining Times
                </label>
                <select
                  multiple
                  value={userInfo.preferences.preferred_times}
                  onChange={(e) => handlePreferenceUpdate('preferred_times', Array.from(e.target.selectedOptions, option => option.value))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    minHeight: '80px'
                  }}
                >
                  {['6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM'].map(time => (
                    <option key={time} value={time} style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                  display: 'block'
                }}>
                  Communication Preference
                </label>
                <select
                  value={userInfo.preferences.communication_preference}
                  onChange={(e) => handlePreferenceUpdate('communication_preference', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                >
                  <option value="email" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                    Email Only
                  </option>
                  <option value="sms" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                    SMS Only
                  </option>
                  <option value="both" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                    Email & SMS
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Save Preferences Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={savePreferences}
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
              color: '#000000',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
      padding: '120px 0 60px 0'
    }}>
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            <User style={{ color: '#d4af37', marginRight: '16px' }} size={40} />
            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '300',
              color: '#ffffff',
              margin: 0,
              fontFamily: "'Playfair Display', serif"
            }}>
              My Account
            </h1>
          </div>
          
          <p style={{
            fontSize: '1.1rem',
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Manage your bookings, preferences, and culinary journey
          </p>

          {/* User Info Summary */}
          {userInfo.name && isCustomerAuthenticated() && (
            <div style={{
              marginTop: '30px',
              padding: '20px',
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              maxWidth: '500px',
              margin: '30px auto 0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ color: '#d4af37', fontSize: '18px', margin: '0 0 4px 0' }}>
                    Welcome back, {userInfo.name.split(' ')[0]}!
                  </h3>
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '0' }}>
                    {userInfo.tier} Member • {userInfo.loyalty_points} Loyalty Points
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Star size={24} style={{ color: '#d4af37', fill: '#d4af37' }} />
                  <button
                    onClick={handleLogout}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.8)',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Tabs - Only show when authenticated and not loading */}
        {!authLoading && isCustomerAuthenticated() && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '40px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {[
              { id: 'bookings', label: 'My Bookings', icon: Calendar },
              { id: 'loyalty', label: 'Loyalty Rewards', icon: Star },
              { id: 'preferences', label: 'Preferences', icon: Settings }
            ].map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: activeTab === tab.id ? '#d4af37' : 'rgba(255, 255, 255, 0.7)',
                    padding: '16px 32px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    borderBottom: activeTab === tab.id ? '2px solid #d4af37' : '2px solid transparent',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <IconComponent size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Tab Content */}
        <div>
          {authLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255, 255, 255, 0.7)' }}>
              <p>Checking authentication...</p>
            </div>
          ) : (
            <>
              {isCustomerAuthenticated() && activeTab === 'bookings' && renderBookingHistory()}
              {isCustomerAuthenticated() && activeTab === 'loyalty' && renderLoyaltySection()}
              {isCustomerAuthenticated() && activeTab === 'preferences' && renderPreferences()}
            </>
          )}
        </div>
      </div>

      {/* Booking Details Modal */}
      {showBookingDetails && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            backgroundColor: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: '1px solid rgba(212, 175, 55, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#ffffff', fontSize: '24px', margin: 0 }}>Booking Details</h3>
              <button
                onClick={() => { setShowBookingDetails(false); setSelectedBooking(null); }}
                style={{ background: 'none', border: 'none', color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.6' }}>
              <p><strong>Service:</strong> {selectedBooking.service_name}</p>
              <p><strong>Date:</strong> {new Date(selectedBooking.booking_date).toLocaleDateString()}</p>
              <p><strong>Status:</strong> <span style={{ 
                color: selectedBooking.status === 'confirmed' ? '#10b981' : 
                      selectedBooking.status === 'pending' ? '#f59e0b' : 
                      selectedBooking.status === 'completed' ? '#3b82f6' : '#ef4444' 
              }}>{selectedBooking.status.charAt(0).toUpperCase() + selectedBooking.status.slice(1)}</span></p>
              
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ color: '#d4af37', marginBottom: '10px' }}>Booking Details:</h4>
                {Object.entries(selectedBooking.booking_details || {}).map(([key, value]) => (
                  <p key={key} style={{ marginBottom: '8px' }}>
                    <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {value || 'Not specified'}
                  </p>
                ))}
              </div>
              
              {selectedBooking.total_cost && (
                <p style={{ marginTop: '15px' }}><strong>Total Cost:</strong> ${selectedBooking.total_cost}</p>
              )}
              
              {selectedBooking.estimated_cost && (
                <p><strong>Estimated Cost:</strong> ${selectedBooking.estimated_cost}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modify Booking Modal */}
      {showModifyModal && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            backgroundColor: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', marginBottom: '15px' }}>Modify Booking</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '20px' }}>
              Update your booking for "{selectedBooking.service_name}" scheduled on {new Date(selectedBooking.booking_date).toLocaleDateString()}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ color: '#d4af37', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                  New Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={modifyForm.start_time}
                  onChange={(e) => setModifyForm({...modifyForm, start_time: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ color: '#d4af37', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                  End Time (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={modifyForm.end_time}
                  onChange={(e) => setModifyForm({...modifyForm, end_time: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ color: '#d4af37', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                  Location
                </label>
                <input
                  type="text"
                  value={modifyForm.location}
                  onChange={(e) => setModifyForm({...modifyForm, location: e.target.value})}
                  placeholder="Enter new location (optional)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ color: '#d4af37', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                  Special Requests
                </label>
                <textarea
                  value={modifyForm.special_requests}
                  onChange={(e) => setModifyForm({...modifyForm, special_requests: e.target.value})}
                  placeholder="Any special requests or changes..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => { 
                  setShowModifyModal(false); 
                  setSelectedBooking(null);
                  setModifyForm({
                    start_time: '',
                    end_time: '',
                    location: '',
                    special_requests: ''
                  });
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={modifyBooking}
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                  border: 'none',
                  color: '#000000',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {showCancelModal && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            backgroundColor: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', marginBottom: '15px' }}>Cancel Booking</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '20px' }}>
              Are you sure you want to cancel your booking for "{selectedBooking.service_name}" on {new Date(selectedBooking.booking_date).toLocaleDateString()}?
            </p>
            <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '20px' }}>
              This action cannot be undone. Please contact us if you need to reschedule instead.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowCancelModal(false); setSelectedBooking(null); }}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Keep Booking
              </button>
              <button
                onClick={cancelBooking}
                style={{
                  background: '#ef4444',
                  border: 'none',
                  color: '#ffffff',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            backgroundColor: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid rgba(212, 175, 55, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#ffffff', fontSize: '20px', margin: 0 }}>Rate Your Experience</h3>
              <button
                onClick={() => { setShowRatingModal(false); setSelectedBooking(null); setRating(0); setReviewComment(''); }}
                style={{ background: 'none', border: 'none', color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '20px' }}>
              How was your {selectedBooking.service_name} experience?
            </p>
            
            {/* Star Rating */}
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '10px', fontSize: '14px' }}>Rating:</p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={28}
                    style={{
                      color: star <= rating ? '#d4af37' : 'rgba(255, 255, 255, 0.3)',
                      fill: star <= rating ? '#d4af37' : 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setRating(star)}
                  />
                ))}
              </div>
              <p style={{ color: '#d4af37', fontSize: '14px' }}>
                {rating === 0 ? 'Click to rate' : 
                 rating === 1 ? 'Poor' :
                 rating === 2 ? 'Fair' :
                 rating === 3 ? 'Good' :
                 rating === 4 ? 'Very Good' : 'Excellent'}
              </p>
            </div>
            
            {/* Review Comment */}
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '10px', fontSize: '14px' }}>Review (optional):</p>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Share your experience..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowRatingModal(false); setSelectedBooking(null); setRating(0); setReviewComment(''); }}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitRating}
                disabled={rating === 0}
                style={{
                  background: rating === 0 ? 'rgba(212, 175, 55, 0.3)' : 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                  border: 'none',
                  color: rating === 0 ? 'rgba(0, 0, 0, 0.5)' : '#000000',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: rating === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                Submit Rating (+50 Loyalty Points)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Authentication Modal */}
      <CustomerAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default CustomerPortal;